import React, { useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency, formatDate, formatRelativeDate } from '@/lib/utils'
import {
  ArrowLeft, Trash2, Phone, Mail, Globe, Plus,
  Briefcase, FileText, Receipt, Users,
  Building2, Check, X, MapPin, Edit2, MessageSquare, Calculator,
  Star, TrendingUp, Sparkles, CheckSquare, CalendarDays,
} from 'lucide-react'
import { toast } from 'sonner'
import LocationModal from './LocationModal'
import { NewContactModal } from './Contacts'
import NoteThread from '@/components/NoteThread'
import TasksThread from '@/components/TasksThread'
import MeetingsThread from '@/components/MeetingsThread'
import TagEditor from '@/components/TagEditor'
import CustomFieldsEditor from '@/components/CustomFieldsEditor'
import ActivityTimeline from '@/components/ActivityTimeline'
import BillingTab from '@/components/BillingTab'

const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Retail', 'Manufacturing', 'Education', 'Real Estate', 'Hospitality', 'Other']
const SIZES = ['1-10', '11-50', '51-200', '201-500', '500+']
const STATUSES = ['active', 'inactive', 'on-hold']

const STATUS_CFG: Record<string, { color: string; bg: string; border: string; label: string }> = {
  active:    { color: 'var(--positive)',      bg: 'var(--positive-dim)',  border: 'color-mix(in srgb, var(--positive) var(--status-tint-strong), transparent)', label: 'Active' },
  inactive:  { color: 'var(--ink-secondary)', bg: 'var(--border-subtle)', border: 'var(--border-default)', label: 'Inactive' },
  'on-hold': { color: 'var(--warning)',       bg: 'var(--warning-dim)',   border: 'color-mix(in srgb, var(--warning) var(--status-tint-strong), transparent)', label: 'On hold' },
}

export function EditableField({
  label, value, onSave, type = 'text', icon: Icon, placeholder,
}: {
  label: string; value: string | null; onSave: (v: string) => void;
  type?: string; icon?: any; placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState('')
  const start = () => { setVal(value || ''); setEditing(true) }
  const save = () => { onSave(val); setEditing(false) }
  const cancel = () => { setEditing(false) }

  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, transition: 'background 120ms', background: editing ? 'var(--hover-bg)' : 'transparent', cursor: editing ? 'default' : 'pointer' }}
      onClick={editing ? undefined : start}
      onMouseEnter={e => { if (!editing) e.currentTarget.style.background = 'var(--surface)' }}
      onMouseLeave={e => { if (!editing) e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {Icon && <Icon style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />}
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      </div>
      {editing ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type={type} value={val} onChange={e => setVal(e.target.value)} placeholder={placeholder} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel() }}
            style={{ flex: 1, padding: '5px 8px', background: 'var(--border-subtle)', border: '1px solid var(--focus-ring)', borderRadius: 6, fontSize: 13, color: 'var(--ink-primary)', outline: 'none' }} />
          <button onClick={save} style={{ padding: '4px 6px', background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 5, color: 'var(--positive)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <Check style={{ width: 11, height: 11 }} />
          </button>
          <button onClick={cancel} style={{ padding: '4px 6px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 5, color: 'var(--ink-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <X style={{ width: 11, height: 11 }} />
          </button>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: value ? 'var(--ink-primary)' : 'var(--ink-tertiary)', wordBreak: 'break-word' }}>
          {value || placeholder || '—'}
        </p>
      )}
    </div>
  )
}

export function EditableSelect({
  label, value, onSave, options, icon: Icon,
}: {
  label: string; value: string | null; onSave: (v: string) => void;
  options: string[]; icon?: any
}) {
  const [editing, setEditing] = useState(false)
  const start = () => setEditing(true)

  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, transition: 'background 120ms', background: editing ? 'var(--hover-bg)' : 'transparent', cursor: editing ? 'default' : 'pointer' }}
      onClick={editing ? undefined : start}
      onMouseEnter={e => { if (!editing) e.currentTarget.style.background = 'var(--surface)' }}
      onMouseLeave={e => { if (!editing) e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {Icon && <Icon style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />}
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      </div>
      {editing ? (
        <select value={value || ''} autoFocus
          onChange={e => { onSave(e.target.value); setEditing(false) }}
          onBlur={() => setEditing(false)}
          style={{ width: '100%', padding: '5px 8px', background: 'var(--border-subtle)', border: '1px solid var(--focus-ring)', borderRadius: 6, fontSize: 13, color: 'var(--ink-primary)', outline: 'none' }}>
          <option value="">—</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: value ? 'var(--ink-primary)' : 'var(--ink-tertiary)' }}>
          {value || '—'}
        </p>
      )}
    </div>
  )
}

function TaxExemptToggle({ exempt, exemptId, onSaveExempt, onSaveId }: {
  exempt: boolean
  exemptId: string | null
  onSaveExempt: (v: boolean) => void
  onSaveId: (v: string) => void
}) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: exempt ? 8 : 0 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tax exempt</span>
        <button onClick={() => onSaveExempt(!exempt)} aria-label="Toggle tax exempt"
          style={{ width: 32, height: 18, borderRadius: 100, background: exempt ? 'var(--positive)' : 'var(--border-default)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 150ms' }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', position: 'absolute', top: 2, left: exempt ? 16 : 2, transition: 'left 150ms' }} />
        </button>
      </div>
      {exempt && (
        <EditableField label="Certificate ID" value={exemptId} onSave={onSaveId} placeholder="Cert. number" />
      )}
    </div>
  )
}

export default function CompanyDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  type TabId = 'timeline' | 'contacts' | 'locations' | 'deals' | 'quotes' | 'invoices' | 'billing' | 'notes' | 'tasks' | 'meetings'
  const VALID_TABS: TabId[] = ['timeline', 'contacts', 'locations', 'deals', 'quotes', 'invoices', 'billing', 'notes', 'tasks', 'meetings']
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab')
  const initialTab: TabId = VALID_TABS.includes(tabFromUrl as TabId) ? (tabFromUrl as TabId) : 'timeline'
  const [activeTab, setActiveTabState] = useState<TabId>(initialTab)
  const setActiveTab = useCallback((next: TabId) => {
    setActiveTabState(next)
    const params = new URLSearchParams(searchParams)
    if (next === 'timeline') params.delete('tab')
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }, [searchParams, setSearchParams])
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState<any>(null)
  const [showNewContactModal, setShowNewContactModal] = useState(false)

  const { data: company, isLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('companies').select('*').eq('id', id).single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'by-company', company?.id],
    queryFn: async () => {
      if (!company || !workspace?.id) return []
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('workspace_id', workspace.id)
        .or(`company_id.eq.${company.id},company_name.eq.${company.name}`)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!company?.id && !!company?.name && !!workspace?.id,
  })

  const { data: locations = [] } = useQuery({
    queryKey: ['company-locations', company?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', company!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!company?.id,
  })

  const { data: deals = [] } = useQuery({
    queryKey: ['company-deals', company?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('company_id', company!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!company?.id,
  })

  const { data: quotes = [] } = useQuery({
    queryKey: ['company-quotes', company?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('company_id', company!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!company?.id,
  })

  const { data: invoices = [] } = useQuery({
    queryKey: ['company-invoices', company?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('company_id', company!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!company?.id,
  })

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { error } = await supabase.from('companies').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      toast.success('Saved')
    },
    onError: (e: any) => toast.error(`Save failed: ${e.message}`),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('companies').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      toast.success('Company deleted')
      navigate('/app/companies')
    },
    onError: (e: any) => toast.error(`Delete failed: ${e.message}`),
  })

  const deleteLocationMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const { error } = await supabase.from('locations').delete().eq('id', locationId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-locations', company?.id] })
      toast.success('Location deleted')
    },
    onError: (e: any) => toast.error(`Delete failed: ${e.message}`),
  })

  // Stamp portal_acknowledged_at = now() so the "added via portal" badge
  // disappears from the row. The provenance flag (created_via_portal)
  // stays for audit; only the ack timestamp flips.
  const acknowledgeLocationMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const { error } = await supabase.from('locations')
        .update({ portal_acknowledged_at: new Date().toISOString() })
        .eq('id', locationId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-locations', company?.id] })
      toast.success('Acknowledged')
    },
    onError: (e: any) => toast.error(`Acknowledge failed: ${e.message}`),
  })

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--canvas)' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
      </div>
    )
  }

  if (!company) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)' }}>Company not found</div>
  }

  const firstContactEmail = contacts.find(c => c.email)?.email || company.email || null
  const websiteUrl = company.website
    ? (company.website.startsWith('http') ? company.website : `https://${company.website}`)
    : null

  const statusCfg = STATUS_CFG[company.status || 'active'] || STATUS_CFG.active

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface-raised)' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/companies') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-secondary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>{company.name}</span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }}>
          {statusCfg.label}
        </span>
        {company.industry && (
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid var(--accent-border)' }}>
            {company.industry}
          </span>
        )}
        <button
          onClick={() => {
            if (confirm(`Delete ${company.name}? This cannot be undone.`)) deleteMutation.mutate()
          }}
          style={{ marginLeft: 'auto', padding: '6px 10px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--danger) var(--status-tint-strong), transparent)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
          <Trash2 style={{ width: 12, height: 12 }} /> Delete
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        <div style={{ width: 280, borderRight: '1px solid var(--border-subtle)', overflow: 'auto', padding: '24px 16px', flexShrink: 0 }}>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 20 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px var(--accent-glow)', marginBottom: 12 }}>
              <Building2 style={{ width: 28, height: 28, color: 'var(--accent-ink)' }} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px', wordBreak: 'break-word' }}>{company.name}</p>
            {company.domain && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>{company.domain}</p>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 20 }}>
            <button
              onClick={() => company.phone && (window.location.href = `tel:${company.phone}`)}
              disabled={!company.phone}
              style={{ padding: '8px 6px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: company.phone ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, cursor: company.phone ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Phone style={{ width: 11, height: 11 }} /> Call
            </button>
            <button
              onClick={() => firstContactEmail && (window.location.href = `mailto:${firstContactEmail}`)}
              disabled={!firstContactEmail}
              title={firstContactEmail ? `Email ${firstContactEmail}` : 'No contact email on file'}
              style={{ padding: '8px 6px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: firstContactEmail ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, cursor: firstContactEmail ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Mail style={{ width: 11, height: 11 }} /> Email
            </button>
            <button
              onClick={() => websiteUrl && window.open(websiteUrl, '_blank', 'noopener,noreferrer')}
              disabled={!websiteUrl}
              style={{ gridColumn: '1 / -1', padding: '8px 6px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: websiteUrl ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, cursor: websiteUrl ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Globe style={{ width: 11, height: 11 }} /> Visit website
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <EditableSelect label="Status" value={company.status}
              onSave={v => updateMutation.mutate({ status: v || 'active' })}
              options={STATUSES} />
            <EditableField label="Client type" value={company.client_type}
              onSave={v => updateMutation.mutate({ client_type: v || null })}
              placeholder="commercial / residential-multi / property-mgmt" />
            <EditableField label="Parent company" value={company.parent_company}
              onSave={v => updateMutation.mutate({ parent_company: v || null })}
              placeholder="Franchise / subsidiary parent" />
            <EditableField label="Email" value={company.email}
              onSave={v => updateMutation.mutate({ email: v || null })}
              icon={Mail} placeholder="contact@example.com" />
            <EditableField label="Phone" value={company.phone}
              onSave={v => updateMutation.mutate({ phone: v || null })}
              icon={Phone} placeholder="+1 555 123 4567" />
            <EditableField label="Domain" value={company.domain}
              onSave={v => updateMutation.mutate({ domain: v || null })}
              icon={Globe} placeholder="example.com" />
            <EditableField label="Website" value={company.website}
              onSave={v => updateMutation.mutate({ website: v || null })}
              icon={Globe} placeholder="https://example.com" />
            <EditableSelect label="Industry" value={company.industry}
              onSave={v => updateMutation.mutate({ industry: v || null })}
              options={INDUSTRIES} icon={Briefcase} />
            <EditableSelect label="Size" value={company.size}
              onSave={v => updateMutation.mutate({ size: v || null })}
              options={SIZES} icon={Users} />
            <EditableField label="Billing terms" value={company.billing_terms}
              onSave={v => updateMutation.mutate({ billing_terms: v || null })}
              placeholder="net-30, net-60, due-on-receipt" />
            <EditableField label="Default payment terms" value={company.default_payment_terms}
              onSave={v => updateMutation.mutate({ default_payment_terms: v || null })}
              placeholder="Free-text fallback" />
            <TaxExemptToggle
              exempt={!!company.tax_exempt}
              exemptId={company.tax_exempt_id}
              onSaveExempt={v => updateMutation.mutate({ tax_exempt: v, ...(v ? {} : { tax_exempt_id: null }) })}
              onSaveId={v => updateMutation.mutate({ tax_exempt_id: v || null })}
            />
          </div>

          {(() => {
            const primary = company.primary_contact_id
              ? contacts.find((c: any) => c.id === company.primary_contact_id)
              : null
            return (
              <div style={{ marginTop: 16, paddingLeft: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <Star style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Primary contact</span>
                </div>
                {primary ? (
                  <button type="button" onClick={() => navigate(`/app/contacts/${primary.id}`)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                    <Star style={{ width: 13, height: 13, color: 'var(--warning)', fill: 'var(--warning)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary.first_name} {primary.last_name}</p>
                      {(primary.email || primary.phone) && (
                        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary.email || primary.phone}</p>
                      )}
                    </div>
                  </button>
                ) : (
                  <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, fontStyle: 'italic' }}>
                    No primary contact{contacts.length > 0 ? ' — star one in the Contacts tab.' : '.'}
                  </p>
                )}
              </div>
            )
          })()}

          <div style={{ marginTop: 16, paddingLeft: 12, paddingRight: 12 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Tags</label>
            <TagEditor value={company.tags} onChange={next => updateMutation.mutate({ tags: next })} />
          </div>

          <div style={{ marginTop: 16, paddingLeft: 12, paddingRight: 12 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Custom fields</label>
            <CustomFieldsEditor value={company.custom_fields} onChange={next => updateMutation.mutate({ custom_fields: next })} />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6, paddingLeft: 12 }}>Quick summary</label>
            <textarea
              defaultValue={company.notes || ''}
              onBlur={e => {
                if ((company.notes || '') === e.target.value) return
                updateMutation.mutate({ notes: e.target.value })
              }}
              placeholder="A one-line summary..."
              rows={4}
              style={{ width: '100%', padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 13, color: 'var(--ink-secondary)', outline: 'none', resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box', fontFamily: 'inherit' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'} />
          </div>

          <PortalTeamSidebarCard companyId={company.id} threshold={company.portal_approval_threshold ?? null} />

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 12 }}>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Created {formatDate(company.created_at)}</p>
            {company.updated_at && company.updated_at !== company.created_at && (
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Updated {formatRelativeDate(company.updated_at)}</p>
            )}
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          <div style={{ display: 'flex', gap: 2, padding: '12px 24px 0', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            {([
              { id: 'timeline',  label: 'Activity',  count: 0,                icon: TrendingUp },
              { id: 'contacts',  label: 'Contacts',  count: contacts.length,  icon: Users },
              { id: 'locations', label: 'Locations', count: locations.length, icon: MapPin },
              { id: 'deals',     label: 'Deals',     count: deals.length,     icon: Briefcase },
              { id: 'quotes',    label: 'Quotes',    count: quotes.length,    icon: FileText },
              { id: 'invoices',  label: 'Invoices',  count: invoices.length,  icon: Receipt },
              { id: 'billing',   label: 'Billing',   count: 0,                icon: Calculator },
              { id: 'notes',     label: 'Notes',     count: 0,                icon: MessageSquare },
              { id: 'tasks',     label: 'Tasks',     count: 0,                icon: CheckSquare },
              { id: 'meetings',  label: 'Meetings',  count: 0,                icon: CalendarDays },
            ] as const).map(tab => {
              const Icon = tab.icon
              const active = activeTab === tab.id
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{ padding: '10px 14px', background: 'none', border: 'none', borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`, color: active ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 120ms' }}>
                  <Icon style={{ width: 13, height: 13 }} />
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{ padding: '1px 6px', background: active ? 'var(--accent-dim)' : 'var(--border-subtle)', color: active ? 'var(--accent-text)' : 'var(--ink-tertiary)', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>

            {activeTab === 'timeline' && company && (
              <ActivityTimeline entityType="company" entityId={company.id} />
            )}

            {activeTab === 'contacts' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button onClick={() => setShowNewContactModal(true)} className="btn-primary">
                    <Plus style={{ width: 14, height: 14 }} /> Add contact
                  </button>
                </div>
                {contacts.length === 0 ? (
                  <EmptyState icon={Users} title="No contacts yet" subtitle="Contacts associated with this company will appear here." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {contacts.map((c: any) => {
                      const isPrimary = company.primary_contact_id === c.id
                      return (
                        <div key={c.id}
                          style={{ padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, transition: 'background 120ms' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                          <button
                            type="button"
                            onClick={() => updateMutation.mutate({ primary_contact_id: isPrimary ? null : c.id })}
                            aria-label={isPrimary ? 'Unset primary contact' : 'Set as primary contact'}
                            title={isPrimary ? 'Primary contact — click to unset' : 'Set as primary'}
                            style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', borderRadius: 6, flexShrink: 0 }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <Star style={{ width: 15, height: 15, color: isPrimary ? 'var(--warning)' : 'var(--ink-tertiary)', fill: isPrimary ? 'var(--warning)' : 'none' }} />
                          </button>
                          <button onClick={() => navigate(`/app/contacts/${c.id}`)}
                            style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-text)' }}>
                                {((c.first_name?.[0] || '') + (c.last_name?.[0] || '')) || c.email?.[0]?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px' }}>
                                {c.first_name} {c.last_name}
                              </p>
                              <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.email || c.phone || '—'}
                              </p>
                            </div>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'locations' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button onClick={() => { setEditingLocation(null); setShowLocationModal(true) }} className="btn-primary">
                    <Plus style={{ width: 14, height: 14 }} /> Add location
                  </button>
                </div>
                {locations.length === 0 ? (
                  <EmptyState icon={MapPin} title="No locations yet" subtitle="Physical sites for this company will appear here." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {locations.map((loc: any) => {
                      const locStatusCfg = STATUS_CFG[loc.status || 'active'] || STATUS_CFG.active
                      return (
                        <div key={loc.id}
                          style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, transition: 'background 120ms' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <MapPin style={{ width: 16, height: 16, color: 'var(--positive)' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{loc.name}</p>
                              {loc.store_number && (
                                <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--ink-tertiary)' }}>{loc.store_number}</span>
                              )}
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: locStatusCfg.bg, color: locStatusCfg.color, border: `1px solid ${locStatusCfg.border}` }}>
                                {locStatusCfg.label}
                              </span>
                              {loc.created_via_portal && !loc.portal_acknowledged_at && (
                                // Unreviewed portal-added row. Acknowledge button is
                                // inline on the badge so staff can dismiss without
                                // leaving the list.
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 4px 2px 8px', borderRadius: 100, background: 'var(--warning-dim)', color: 'var(--warning)', border: '1px solid var(--warning-border)' }}>
                                  <Sparkles style={{ width: 10, height: 10 }} />
                                  New — added via portal
                                  <button onClick={() => acknowledgeLocationMutation.mutate(loc.id)}
                                    disabled={acknowledgeLocationMutation.isPending}
                                    aria-label="Acknowledge"
                                    title="Mark as reviewed"
                                    style={{ marginLeft: 2, background: 'var(--warning)', border: 'none', borderRadius: 100, color: 'var(--accent-ink)', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                                    <Check style={{ width: 10, height: 10 }} />
                                  </button>
                                </span>
                              )}
                            </div>
                            <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {loc.address_line1}{loc.address_line2 ? `, ${loc.address_line2}` : ''} · {loc.city}, {loc.state} {loc.zip}
                            </p>
                            {(loc.region || loc.district) && (
                              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
                                {[loc.region, loc.district].filter(Boolean).join(' / ')}
                              </p>
                            )}
                          </div>
                          <button onClick={() => { setEditingLocation(loc); setShowLocationModal(true) }} aria-label="Edit location"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 6, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-primary)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                            <Edit2 style={{ width: 14, height: 14 }} />
                          </button>
                          <button onClick={() => { if (confirm(`Delete location "${loc.name}"?`)) deleteLocationMutation.mutate(loc.id) }} aria-label="Delete location"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 6, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                            <Trash2 style={{ width: 14, height: 14 }} />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'deals' && (
              deals.length === 0
                ? <EmptyState icon={Briefcase} title="No deals yet" subtitle="Deals linked to this company will appear here." />
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {deals.map((d: any) => (
                      <button key={d.id} onClick={() => navigate(`/app/deals/${d.id}`)}
                        style={{ textAlign: 'left', padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'background 120ms' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{d.title || 'Untitled deal'}</p>
                          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>{d.stage || '—'}</p>
                        </div>
                        {typeof d.value === 'number' && (
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{formatCurrency(d.value)}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )
            )}

            {activeTab === 'quotes' && (
              quotes.length === 0
                ? <EmptyState icon={FileText} title="No quotes yet" subtitle="Quotes for this company will appear here." />
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {quotes.map((q: any) => (
                      <button key={q.id} onClick={() => navigate(`/app/quotes/${q.id}`)}
                        style={{ textAlign: 'left', padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'background 120ms' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px', fontFamily: 'monospace' }}>{q.quote_number}</p>
                          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, textTransform: 'capitalize' }}>{q.status}</p>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{formatCurrency(q.total || 0)}</span>
                      </button>
                    ))}
                  </div>
                )
            )}

            {activeTab === 'invoices' && (
              invoices.length === 0
                ? <EmptyState icon={Receipt} title="No invoices yet" subtitle="Invoices for this company will appear here." />
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {invoices.map((inv: any) => (
                      <button key={inv.id} onClick={() => navigate(`/app/invoices/${inv.id}`)}
                        style={{ textAlign: 'left', padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'background 120ms' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px', fontFamily: 'monospace' }}>{inv.invoice_number}</p>
                          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, textTransform: 'capitalize' }}>{inv.status}</p>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{formatCurrency(inv.total || 0)}</span>
                      </button>
                    ))}
                  </div>
                )
            )}

            {activeTab === 'billing' && company && (
              <BillingTab contactIds={contacts.map((c: any) => c.id)} />
            )}

            {activeTab === 'notes' && company && (
              <NoteThread entityType="company" entityId={company.id} />
            )}

            {activeTab === 'tasks' && company && (
              <TasksThread entityType="company" entityId={company.id} />
            )}

            {activeTab === 'meetings' && company && (
              <MeetingsThread entityType="company" entityId={company.id} />
            )}

          </div>
        </div>
      </div>

      <AnimatePresence>
        {showLocationModal && (
          <LocationModal
            onClose={() => { setShowLocationModal(false); setEditingLocation(null) }}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['company-locations', company?.id] })}
            companyId={company.id}
            existingLocation={editingLocation}
          />
        )}
        {showNewContactModal && company && (
          <NewContactModal
            initialCompany={{ id: company.id, name: company.name }}
            onClose={() => setShowNewContactModal(false)}
            onCreated={() => {
              // ['contacts'] prefix-matches every hierarchical contact key
              // from d645a06, including ['contacts', 'by-company', company?.id]
              // — list on this page refreshes without manual reload.
              queryClient.invalidateQueries({ queryKey: ['contacts'] })
              setShowNewContactModal(false)
            }}
            // contactCount={0} — plan-limit gating via this prop is not
            // currently enforced (tier contact-count gates unwired per
            // honesty-pass findings). Avoiding a workspace-wide count query
            // that would feed an inactive gate.
            contactCount={0}
          />
        )}
      </AnimatePresence>

    </div>
  )
}

// Read-only portal team summary + threshold control. Staff trigger the
// same set_threshold edge function the portal admins use — single rules
// engine. Lives in the company sidebar so it's accessible regardless of
// which tab the operator is on.
function PortalTeamSidebarCard({ companyId, threshold }: { companyId: string; threshold: number | null }) {
  const queryClient = useQueryClient()
  const { data: roles = { admin: 0, member: 0, viewer: 0, total: 0 } } = useQuery({
    queryKey: ['portal-team-summary', companyId],
    queryFn: async () => {
      const { data } = await supabase.from('portal_users').select('role').eq('company_id', companyId)
      const rows = (data ?? []) as Array<{ role: string }>
      const out = { admin: 0, member: 0, viewer: 0, total: rows.length }
      for (const r of rows) {
        if (r.role === 'admin') out.admin++
        else if (r.role === 'member') out.member++
        else if (r.role === 'viewer') out.viewer++
      }
      return out
    },
  })

  const onSetThreshold = async () => {
    const raw = prompt('Approval threshold (members can approve quotes ≤ this amount). Leave empty to clear.', threshold !== null ? String(threshold) : '')
    if (raw === null) return  // Cancel
    const trimmed = raw.trim()
    let newVal: number | null = null
    if (trimmed !== '') {
      const parsed = Number(trimmed)
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error('Threshold must be a non-negative number')
        return
      }
      newVal = parsed
    }
    const { data, error } = await supabase.functions.invoke<{ ok: boolean; threshold: number | null; error?: string }>(
      'portal-invite',
      { body: { action: 'set_threshold', company_id: companyId, threshold: newVal } },
    )
    if (error || data?.error) {
      toast.error(data?.error || error?.message || 'Failed to set threshold')
      return
    }
    toast.success(newVal === null ? 'Threshold cleared' : `Threshold set to ${formatCurrency(newVal, 'USD')}`)
    queryClient.invalidateQueries({ queryKey: ['company', companyId] })
  }

  if (roles.total === 0) return null

  return (
    <div style={{ marginTop: 20, paddingTop: 16, paddingLeft: 12, borderTop: '1px solid var(--border-subtle)' }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Portal team</p>
      <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0 }}>
        {roles.total} {roles.total === 1 ? 'user' : 'users'}
        {(roles.admin > 0 || roles.member > 0 || roles.viewer > 0) && ' · '}
        {[roles.admin && `${roles.admin} admin`, roles.member && `${roles.member} member`, roles.viewer && `${roles.viewer} viewer`].filter(Boolean).join(', ')}
      </p>
      <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '8px 0 4px' }}>
        Approval threshold: {threshold !== null ? <strong style={{ color: 'var(--ink-primary)' }}>{formatCurrency(threshold, 'USD')}</strong> : 'not set'}
      </p>
      <button type="button" onClick={onSetThreshold}
        style={{ background: 'transparent', border: 'none', color: 'var(--accent-text)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
        {threshold !== null ? 'Change threshold' : 'Set threshold'}
      </button>
    </div>
  )
}

function EmptyState({ icon: Icon, title, subtitle }: { icon: any; title: string; subtitle: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Icon style={{ width: 22, height: 22, color: 'var(--ink-tertiary)' }} />
      </div>
      <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>{title}</p>
      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, maxWidth: 260, lineHeight: 1.5 }}>{subtitle}</p>
    </div>
  )
}
